import { useMemo, useState } from 'react';
import { OwnershipReasonEnum } from '../models/enum/ownership-reason.enum';
import { LoanTypeEnum } from '../models/enum/loan-type.enum';
import { SchemeTypeEnum } from '../models/enum/scheme-type.enum';
import { HousingSchemeChecking, checkIsFlatResale } from '../utils/form-helper';
import { StructureStatusEnum } from '../models/enum/structure-status.enum';
import { ApplicationStep1 } from '../models/classes/ApplicationStep1.class';
import { ApplicationStep2 } from '../models/classes/ApplicationStep2.class';
import { ApplicationStart } from '../models/classes/ApplicationStart.class';
import { ApplicationStep3 } from '../models/classes/ApplicationStep3.class';
import { SolicitorTypeEnum } from '../models/enum/solicitor-type.enum';
import { ResidentialStatusEnum } from '../models/enum/residential-status.enum';

function useCheckRemission(
  applicationStartFormData: ApplicationStart,
  applicationStep1FormData: ApplicationStep1,
  applicationStep2FormData: ApplicationStep2,
  applicationStep3FormData: ApplicationStep3
) {
  const [newLoanType, setNewLoanType] = useState<LoanTypeEnum | undefined>(applicationStep2FormData.new_loan_type);

  const schemeType = useMemo(() => {
    return HousingSchemeChecking(applicationStep1FormData).scheme_type;
  }, [applicationStep1FormData]);

  const isFlatResale = useMemo(() => {
    return checkIsFlatResale(applicationStartFormData.selected_hdb_property?.financing_info.sales_type_code!);
  }, [applicationStartFormData.selected_hdb_property?.financing_info.sales_type_code]);
  const isProposedLoanTypeHdb = newLoanType === LoanTypeEnum.hdb_loan;

  const isReasonDemiseOrDivorce =
    applicationStep1FormData.reason.change_ownership_reason === OwnershipReasonEnum.divorce ||
    applicationStep1FormData.reason.change_ownership_reason === OwnershipReasonEnum.demise_of_current_owner;

  const isFullSPRHouseHold = applicationStep1FormData.household.incoming_owners.every(
    (e) => e.myinfo.residential_status === ResidentialStatusEnum.pr
  );

  // It is fully HDB if enum is hdb_solicitor
  const isProposedSolicitorFullyHdb = applicationStep3FormData.proposed_solicitor_type === SolicitorTypeEnum.hdb_solicitor;

  const privateConveyancing = applicationStep3FormData.proposed_solicitor_type === SolicitorTypeEnum.private_solicitor;
  const directPrivateConveyancing = applicationStep3FormData.proposed_solicitor_type === SolicitorTypeEnum.conveyancing_private_solicitor;
  const isPrivateConveyancing = privateConveyancing || directPrivateConveyancing;

  const isJointSingleScheme = schemeType === SchemeTypeEnum.joint_single_schema;

  const mainApplicantUinfin = applicationStartFormData.selected_hdb_property?.main_applicant_uinfin;
  const hasIncomingProposedOwner = applicationStep1FormData.household.incoming_owners.some(
    (e) => e.structure_status === StructureStatusEnum.incoming
  );
  const mainApplicantRetains = !!mainApplicantUinfin
    && applicationStep1FormData.household.owners.some((e) => e.myinfo.uinfin === mainApplicantUinfin);
  const hasRetainingIncomingOwner = applicationStep1FormData.household.incoming_owners.some(
    (e) => e.structure_status === StructureStatusEnum.retaining
  );
  const isOutrightTransfer =
    !hasRetainingIncomingOwner && !(hasIncomingProposedOwner && mainApplicantRetains);

  const isRemission = useMemo(() => {
    if (isFlatResale && isProposedLoanTypeHdb) return true;

    if (!isReasonDemiseOrDivorce && isFullSPRHouseHold && isProposedSolicitorFullyHdb) {
      return false;
    }

    if (isOutrightTransfer) return false;
    if (isPrivateConveyancing) return true;
    if (isJointSingleScheme) return false;
    return true;
  }, [
    isFlatResale,
    isProposedLoanTypeHdb,
    isReasonDemiseOrDivorce,
    isFullSPRHouseHold,
    isProposedSolicitorFullyHdb,
    isOutrightTransfer,
    isPrivateConveyancing,
    isJointSingleScheme,
  ]);

  return { isRemission: isRemission, displayValuation: !isRemission, setNewLoanType: setNewLoanType, schemeType };
}

export default useCheckRemission;
